import { sameSquare } from './board';
import type { Constants, CrownMode, Face, PatternName, ScoreBreakdown, Square } from './types';

/** Best single pattern for a list of link faces (§11.1). */
export function patternOf(
  faces: readonly Face[],
  constants: Pick<Constants, 'BONUS_LADDER' | 'BONUS_ECHO' | 'BONUS_STREAK'>,
): { pattern: PatternName | null; bonus: number } {
  if (faces.length < 2) return { pattern: null, bonus: 0 };
  const first = faces[0] as Face;
  const sameColor = faces.every((f) => f.color === first.color);
  const sameNumber = faces.every((f) => f.number === first.number);
  const ladder =
    faces.length === 3 &&
    sameColor &&
    faces[0]?.number === 1 &&
    faces[1]?.number === 2 &&
    faces[2]?.number === 3;
  const candidates: { pattern: PatternName; bonus: number }[] = [];
  if (ladder) candidates.push({ pattern: 'ladder', bonus: constants.BONUS_LADDER });
  if (sameNumber) candidates.push({ pattern: 'echo', bonus: constants.BONUS_ECHO });
  if (sameColor) candidates.push({ pattern: 'streak', bonus: constants.BONUS_STREAK });
  let best: { pattern: PatternName | null; bonus: number } = { pattern: null, bonus: 0 };
  for (const c of candidates) if (best.pattern === null || c.bonus > best.bonus) best = c;
  return best;
}

/** Collinearity test of §11.2: the start square and landing squares lie on one line. */
export function isCollinear(start: Square, landings: readonly Square[]): boolean {
  const q = landings.find((p) => !sameSquare(p, start));
  if (!q) return true;
  const ax = q.x - start.x;
  const ay = q.y - start.y;
  return landings.every((p) => ax * (p.y - start.y) - ay * (p.x - start.x) === 0);
}

/** Full Circle (§11.1): ends on its start square, and start + landings are not collinear. */
export const isFullCircle = (
  start: Square,
  end: Square | null,
  landings: readonly Square[],
): boolean => !!end && sameSquare(start, end) && !isCollinear(start, landings);

export interface ScoreInputs {
  readonly faces: readonly Face[];
  readonly newLandings: number;
  readonly captures: number;
  readonly crowned: boolean;
  readonly start: Square;
  readonly end: Square | null;
  readonly landings: readonly Square[];
}

/** Chain score P × M (§11.1); also used live for Epitaph replays (§11.3). */
export function scoreChain(
  inputs: ScoreInputs,
  constants: Constants,
  crownMode: CrownMode,
): ScoreBreakdown {
  const landing = inputs.newLandings * constants.POINTS_LANDING;
  const capture = inputs.captures * constants.POINTS_CAPTURE;
  const crown = inputs.crowned && crownMode === 'immediate' ? constants.POINTS_CROWN : 0;
  const pTotal = landing + capture + crown;
  const { pattern, bonus } = patternOf(inputs.faces, constants);
  const fullCircle = isFullCircle(inputs.start, inputs.end, inputs.landings);
  const fullCircleBonus = fullCircle ? constants.BONUS_FULL_CIRCLE : 0;
  const mTotal = inputs.faces.length + bonus + fullCircleBonus;
  return {
    points: {
      landings: inputs.newLandings,
      landing,
      captures: inputs.captures,
      capture,
      crown,
      total: pTotal,
    },
    multiplier: {
      links: inputs.faces.length,
      pattern,
      patternBonus: bonus,
      fullCircle,
      fullCircleBonus,
      total: mTotal,
    },
    score: pTotal * mTotal,
  };
}
